import type { MessageDescriptor } from "@lingui/core";

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useCallback, useState } from "react";

import type { DeleteBlockedError, DeleteImpactReport } from "../../../api";

import { ErrorCode } from "../../../api";
import { apiErrorCode, RequestError } from "../../../api/api-connector";
import { Button } from "../../../components/ui-kit/button";
import { useDialog } from "../../../components/ui-kit/dialog-provider";
import { resolveEnumLabel } from "../../../components/ui-kit/fields/utils";
import { AdminConsole, archiveConsequences } from "../admin-console";
import { UsersItem } from "./users";

/**
 * Deleting a user from the users table: read what the delete would destroy,
 * show it, and only then let it through. Returns the two halves `DataTable`
 * wants — `confirmDelete` runs before its delete, `deleteUser` performs it.
 */
export function useDeleteUserFlow(admin: AdminConsole) {
  const { openDialog } = useDialog();
  const { t } = useLingui();

  const showReport = useCallback(
    (item: UsersItem, report: DeleteImpactReport) => {
      const outcome = outcomeOf(report);

      return openDialog({
        title: t`Delete ${item.username}?`,
        className: "sm:max-w-xl",
        body: <DeleteImpactBody report={report} outcome={outcome} />,
        footer: (close) => (
          <ReportFooter
            outcome={outcome}
            close={close}
            onArchive={() => admin.archiveUser(item.uuid)}
          />
        ),
      });
    },
    [openDialog, admin, t],
  );

  const confirmDelete = useCallback(
    async (item: UsersItem) =>
      showReport(item, await admin.deleteImpact(item.uuid)),
    [admin, showReport],
  );

  /**
   * The report `confirmDelete` showed is a snapshot, so rows can appear between
   * the two calls and the `DELETE` can still be refused. Re-render the same
   * dialog from the report the 409 carries; anything else is somebody else's
   * error to explain.
   */
  const deleteUser = useCallback(
    async (item: UsersItem) => {
      try {
        await admin.deleteUser(item.uuid);
      } catch (error) {
        if (apiErrorCode(error) !== ErrorCode.DELETE_BLOCKED) {
          throw error;
        }

        const report = blockedReport(error);

        // A refusal the report itself doesn't account for would open a dialog
        // whose only action is a delete that was just refused; let the global
        // handler state the reason instead.
        if (!report || outcomeOf(report) === "deletable") {
          throw error;
        }

        await showReport(item, report);
      }
    },
    [admin, showReport],
  );

  return { confirmDelete, deleteUser };
}

/**
 * The actions the report allows, with the archive one held open until it
 * answers: the dialog is the only thing on screen, so it has to show that the
 * click landed, and a second one must not reach the API.
 */
function ReportFooter({
  outcome,
  close,
  onArchive,
}: {
  outcome: Outcome;
  close: (value: boolean) => void;
  onArchive: () => Promise<void>;
}) {
  const [archiving, setArchiving] = useState(false);

  return (
    <div className="flex justify-end gap-2">
      <Button
        variant="subtle"
        disabled={archiving}
        onClick={() => close(false)}
      >
        <Trans>Cancel</Trans>
      </Button>

      {outcome === "blocked" ? null : outcome === "archive-first" ? (
        <Button
          disabled={archiving}
          onClick={async () => {
            setArchiving(true);
            // Closing regardless: a failure reaches the global handler, which
            // opens its own dialog over this one and orphans the resolver this
            // promise is waiting on.
            try {
              await onArchive();
            } finally {
              close(false);
            }
          }}
        >
          <Trans>Archive instead</Trans>
        </Button>
      ) : (
        <Button onClick={() => close(true)}>
          <Trans>Delete permanently</Trans>
        </Button>
      )}
    </div>
  );
}

/**
 * What the report leaves the admin able to do. Read once per report, so the
 * explanation and the action it sits under cannot disagree about which of the
 * three states this is.
 */
type Outcome = "blocked" | "archive-first" | "deletable";

function outcomeOf(report: DeleteImpactReport): Outcome {
  if (hasRows(report.blockers)) {
    return "blocked";
  }

  return report.requires_archive_first ? "archive-first" : "deletable";
}

/**
 * Each category of the report, in the order an admin needs them: why it is
 * refused, then the loss archival exists to prevent, then the rest.
 */
const CATEGORIES: Array<{
  key: keyof Omit<
    DeleteImpactReport,
    "user_uuid" | "username" | "requires_archive_first"
  >;
  label: MessageDescriptor;
}> = [
  { key: "blockers", label: msg`Blocks the delete` },
  { key: "loses_attribution", label: msg`Would lose its author` },
  { key: "deleted", label: msg`Deleted along with the account` },
  { key: "unlinked", label: msg`Unlinked from the account` },
  { key: "transfers_to_shadow", label: msg`Reassigned to the shadow user` },
  { key: "links_cleared", label: msg`Links cleared` },
];

function DeleteImpactBody({
  report,
  outcome,
}: {
  report: DeleteImpactReport;
  outcome: Outcome;
}) {
  const populated = CATEGORIES.filter(({ key }) => hasRows(report[key]));

  return (
    <div className="flex flex-col gap-4 text-sm">
      {outcome === "blocked" ? (
        <p className="text-danger-foreground">
          <Trans>
            <strong>{report.username}</strong> cannot be deleted: rows elsewhere
            still point at this account and the database will not orphan them.
            Re-own or remove them first.
          </Trans>
        </p>
      ) : outcome === "archive-first" ? (
        <div className="flex flex-col gap-2">
          <p>
            <Trans>
              <strong>{report.username}</strong> has authored records. Deleting
              the account strips their name off every one of them, permanently.
            </Trans>
          </p>
          <p>{resolveEnumLabel(archiveConsequences)}</p>
        </div>
      ) : (
        <p>
          <Trans>
            Deleting <strong>{report.username}</strong> cannot be undone. Here
            is what it affects.
          </Trans>
        </p>
      )}

      {populated.length === 0 ? (
        <p className="text-muted-foreground">
          <Trans>Nothing else in the database references this account.</Trans>
        </p>
      ) : (
        populated.map(({ key, label }) => (
          <div key={key}>
            <h5 className="mb-1 font-medium">{resolveEnumLabel(label)}</h5>
            <ul className="text-muted-foreground flex flex-col gap-0.5">
              {Object.entries(report[key] ?? {}).map(([table, count]) => (
                <li key={table} className="flex justify-between gap-4">
                  <span className="font-mono text-xs">{table}</span>
                  <span className="tabular-nums">{count}</span>
                </li>
              ))}
            </ul>
          </div>
        ))
      )}
    </div>
  );
}

function hasRows(counts: Record<string, number> | undefined) {
  return !!counts && Object.keys(counts).length > 0;
}

/**
 * The report a 409 `DELETE_BLOCKED` carries, nested under `data` — unlike
 * `GET /delete-impact`, which serves the report's own fields at the top level.
 */
function blockedReport(error: unknown): DeleteImpactReport | null {
  const body = error instanceof RequestError ? error.data : null;

  return body && typeof body === "object" && "data" in body
    ? (body as DeleteBlockedError).data
    : null;
}
